import { Injectable } from '@angular/core';

// ── Still-frame selection (anti-motion-blur) ──────────────────────────────────
// The old shutter grabbed ONE frame the instant the hold timer expired, so any
// head/hand twitch at that exact moment produced a blurred upload. Instead, every
// detection frame during the hold is sampled: the already-normalized crop is
// kept in a small ring of reusable canvases and its face landmarks are stored in
// pixel space. For each frame we measure landmark VELOCITY (px/ms) to the
// previous frame, then score the frame by the mean velocity over a centered
// window of WINDOW frames (the frame is only as still as its neighbours). The
// stillest frame wins; a native-res eye-patch sharpness (Laplacian variance)
// breaks near-ties and also catches autofocus hunting, which motion cannot see.
// Frames with closed eyes are never candidates. Memory is bounded: only the
// newest HALF+1 frames need their pixels (a frame's image is only needed until
// its own window completes), plus the single best-so-far canvas.

export interface StillFrameSample {
  t: number; // performance.now() at detection
  landmarks: { x: number; y: number }[]; // normalized (0..1) mediapipe points
  frameW: number;
  frameH: number;
  blink: boolean; // eyes closed on this frame -> never a candidate
  // Draws the upload image for this frame into the given canvas (the caller owns
  // the crop math). Only invoked when the frame can be a candidate.
  draw: (target: HTMLCanvasElement) => void;
  // Sharpness of the frame (higher = sharper). Optional; 0 disables the
  // tie-break for this frame.
  sharpness?: number;
}

export interface StillFrameBest {
  canvas: HTMLCanvasElement;
  t: number;
  motion: number; // mean px/ms over the window
  sharpness: number;
  score: number; // combined, lower = better
  index: number; // sample ordinal since reset()
}

export interface StillFrameStats {
  samples: number;
  candidates: number; // samples that were scored (window complete, eligible)
  bestIndex: number | null;
  bestMotion: number | null;
  bestSharpness: number | null;
  bestAgeMs: number | null; // how far before "now" the chosen frame was taken
  fps: number | null;
}

interface Slot {
  t: number;
  pts: Float32Array; // px coords, [x0,y0,x1,y1,...]
  velocity: number; // px/ms vs previous sample; Infinity = chain break
  blink: boolean;
  sharpness: number;
  canvas: HTMLCanvasElement | null;
  index: number;
}

@Injectable({ providedIn: 'root' })
export class StillFrameSelectorService {
  // Window = 5 frames (center +/- 2): a frame counts as still only if its 4
  // surrounding inter-frame motions are all small.
  private readonly HALF = 2;
  private get WINDOW() {
    return this.HALF * 2 + 1;
  }
  // A gap longer than this between two detections means we were off-gate in
  // between (not green); velocity across the gap is meaningless -> chain break.
  // Generous on purpose: slow phones sample at 3-6 fps during the hold and
  // must still get candidates (a 400ms cap left them with none).
  private readonly MAX_EDGE_GAP_MS = 1000;
  // Velocity noise floor (px/ms). ~0.6px at 30fps: below this every frame is
  // "still" and only sharpness should decide.
  private readonly MOTION_FLOOR = 0.02;

  private samples: Slot[] = [];
  private pool: HTMLCanvasElement[] = [];
  private best: StillFrameBest | null = null;
  private ordinal = 0;
  private candidates = 0;
  private firstT = 0;
  private lastT = 0;

  reset(): void {
    for (const s of this.samples) {
      if (s.canvas) this.release(s.canvas);
    }
    this.samples = [];
    if (this.best) {
      this.release(this.best.canvas);
      this.best = null;
    }
    // Give the pixel memory back between holds (iOS Safari budgets canvas
    // memory per page; a few multi-MB canvases idling is what blows it).
    for (const c of this.pool) {
      c.width = 0;
      c.height = 0;
    }
    this.pool = [];
    this.ordinal = 0;
    this.candidates = 0;
    this.firstT = 0;
    this.lastT = 0;
  }

  // Alias kept for the component's destroy hook.
  dispose(): void {
    this.reset();
  }

  hasBest(): boolean {
    return !!this.best;
  }

  // Returns the best frame so far and hands ownership of its canvas to the
  // caller (it will not be reused by the pool). Null when nothing eligible yet.
  takeBest(): StillFrameBest | null {
    const b = this.best;
    if (!b) return null;
    this.best = null;
    // Everything else is stale once a shot is taken.
    this.reset();
    return b;
  }

  stats(): StillFrameStats {
    const span = this.lastT - this.firstT;
    return {
      samples: this.ordinal,
      candidates: this.candidates,
      bestIndex: this.best ? this.best.index : null,
      bestMotion: this.best ? round(this.best.motion, 4) : null,
      bestSharpness: this.best ? round(this.best.sharpness, 1) : null,
      bestAgeMs: this.best ? Math.round(this.lastT - this.best.t) : null,
      fps:
        this.ordinal > 1 && span > 0
          ? round(((this.ordinal - 1) * 1000) / span, 1)
          : null,
    };
  }

  push(sample: StillFrameSample): void {
    const n = sample.landmarks.length;
    const pts = new Float32Array(n * 2);
    for (let i = 0; i < n; i++) {
      pts[i * 2] = sample.landmarks[i].x * sample.frameW;
      pts[i * 2 + 1] = sample.landmarks[i].y * sample.frameH;
    }

    const prev = this.samples[this.samples.length - 1];
    let velocity = Infinity; // first frame / chain break
    if (prev && prev.pts.length === pts.length) {
      const dt = sample.t - prev.t;
      if (dt > 0 && dt <= this.MAX_EDGE_GAP_MS) {
        let sum = 0;
        for (let i = 0; i < n; i++) {
          const dx = pts[i * 2] - prev.pts[i * 2];
          const dy = pts[i * 2 + 1] - prev.pts[i * 2 + 1];
          sum += Math.sqrt(dx * dx + dy * dy);
        }
        velocity = sum / n / Math.max(dt, 1);
      }
    }

    // Only eligible frames pay for a pixel copy.
    let canvas: HTMLCanvasElement | null = null;
    if (!sample.blink) {
      canvas = this.acquire();
      sample.draw(canvas);
    }

    const slot: Slot = {
      t: sample.t,
      pts,
      velocity,
      blink: sample.blink,
      sharpness: sample.sharpness || 0,
      canvas,
      index: this.ordinal++,
    };
    if (!this.firstT) this.firstT = sample.t;
    this.lastT = sample.t;

    this.samples.push(slot);
    if (this.samples.length > this.WINDOW) {
      const old = this.samples.shift();
      if (old?.canvas) this.release(old.canvas); // should already be null
    }

    if (this.samples.length === this.WINDOW) {
      this.judge(this.samples[this.HALF]);
    }
  }

  // Score the window center and keep it if it beats the best so far. Its image
  // is not needed by anyone afterwards, so it either becomes "best" (pointer
  // swap, no copy) or goes back to the pool.
  private judge(center: Slot): void {
    // Window edges: velocities of samples[1..WINDOW-1] (velocity is "vs previous").
    let sum = 0;
    let finite = true;
    for (let i = 1; i < this.samples.length; i++) {
      const v = this.samples[i].velocity;
      if (!isFinite(v)) {
        finite = false;
        break;
      }
      sum += v;
    }
    const motion = finite ? sum / (this.samples.length - 1) : Infinity;

    if (!center.canvas || !finite) {
      if (center.canvas) this.release(center.canvas);
      center.canvas = null;
      return;
    }

    this.candidates++;
    const score = (motion + this.MOTION_FLOOR) / Math.sqrt(center.sharpness + 1);

    if (!this.best || score < this.best.score) {
      if (this.best) this.release(this.best.canvas);
      this.best = {
        canvas: center.canvas,
        t: center.t,
        motion,
        sharpness: center.sharpness,
        score,
        index: center.index,
      };
    } else {
      this.release(center.canvas);
    }
    center.canvas = null;
  }

  private acquire(): HTMLCanvasElement {
    return this.pool.pop() || document.createElement('canvas');
  }

  private release(c: HTMLCanvasElement): void {
    if (this.pool.length < this.HALF + 3) {
      this.pool.push(c);
    } else {
      c.width = 0;
      c.height = 0;
    }
  }
}

// ── Sharpness: Laplacian variance of a native-res patch ───────────────────────
// Computed on a small patch drawn 1:1 from the source (eyes have the highest
// contrast detail on a face). Comparable across frames of one hold because the
// face size is constant there. Higher = sharper.
export function laplacianVariance(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number
): number {
  if (w < 3 || h < 3) return 0;
  const data = ctx.getImageData(0, 0, w, h).data;
  const gray = new Float32Array(w * h);
  for (let i = 0, p = 0; i < gray.length; i++, p += 4) {
    gray[i] = 0.299 * data[p] + 0.587 * data[p + 1] + 0.114 * data[p + 2];
  }
  let sum = 0;
  let sumSq = 0;
  let count = 0;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      const lap =
        4 * gray[i] - gray[i - 1] - gray[i + 1] - gray[i - w] - gray[i + w];
      sum += lap;
      sumSq += lap * lap;
      count++;
    }
  }
  if (!count) return 0;
  const mean = sum / count;
  return sumSq / count - mean * mean;
}

function round(v: number, digits: number): number {
  const f = Math.pow(10, digits);
  return Math.round(v * f) / f;
}
